import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Filter,
  X,
  Check,
  ChevronsUpDown,
  Search,
  Loader2,
  AlertCircle,
  BarChart3,
  CalendarRange,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { KpiBasisLabel, KpiBasisNote } from "@/components/reports/KpiBasisLabel";
import {
  useSalesCommissionChart,
  useChartFilterOptions,
} from "@/hooks/useInvoices";
import { canManageAgencyUsers, isAgencyOwnerRole } from "@/lib/roles";
import { useSnapshotPrint } from "@/context/SnapshotPrintContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SalesCommissionChartProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
  userRole?: string | null;
  /** Global Reports filter on invoice trip return date (yyyy-mm-dd) */
  returnDateFrom?: string;
  returnDateTo?: string;
}

interface ChartFilters {
  dateFrom: string;
  dateTo: string;
  suppliers: string[];
  travelTypes: string[];
  advisorIds: number[];
  showSales: boolean;
  showCommission: boolean;
  includeOpen: boolean;
}

const MONTH_LABELS = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

const COLORS: Record<string, string> = {
  commission: "#e8792a",
  sales: "#6b2f0a",
  advisorCommission: "#0d9488",
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatYAxisTick(value: number): string {
  if (value === 0) return "$0";
  return `$${new Intl.NumberFormat("en-US").format(value)}`;
}

function formatMonthLabel(month: string, includeYear: boolean): string {
  const [yearStr, m] = month.split("-");
  const idx = parseInt(m, 10) - 1;
  const label = MONTH_LABELS[idx] ?? month;
  if (includeYear && yearStr?.length === 4) {
    return `${label} ${yearStr}`;
  }
  return label;
}

function chartRangeSpansMultipleYears(dateFrom: string, dateTo: string): boolean {
  const partsFrom = dateFrom.split("-");
  const partsTo = dateTo.split("-");
  if (partsFrom.length < 1 || partsTo.length < 1) return false;
  const y1 = parseInt(partsFrom[0], 10);
  const y2 = parseInt(partsTo[0], 10);
  return Number.isFinite(y1) && Number.isFinite(y2) && y1 !== y2;
}

function currentYearRange(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const year = now.getFullYear();
  return {
    dateFrom: `${year}-01-01`,
    dateTo: `${year}-12-31`,
  };
}
// ---------------------------------------------------------------------------
// Multi-select filter popover
// ---------------------------------------------------------------------------

function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  searchPlaceholder,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (values: string[]) => void;
  searchPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = useCallback(
    (value: string) => {
      const next = selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value];
      onChange(next);
    },
    [selected, onChange],
  );

  const hasSelection = selected.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={`${label}, ${hasSelection ? `${selected.length} selected` : "none selected"}`}
          className={`h-8 gap-1.5 text-xs ${
            hasSelection
              ? "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
              : ""
          }`}
        >
          {label}
          {hasSelection && (
            <span className="ml-1 rounded-full bg-orange-200 px-1.5 text-xs font-semibold dark:bg-orange-800">
              {selected.length}
            </span>
          )}
          <ChevronsUpDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              aria-label={searchPlaceholder || `Search ${label.toLowerCase()}`}
              placeholder={searchPlaceholder || `Search ${label.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-7 text-xs"
            />
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">
              No results found
            </p>
          ) : (
            filtered.map((option) => {
              const isSelected = selected.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="checkbox"
                  aria-checked={isSelected}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
                  onClick={() => toggle(option.value)}
                >
                  <div
                    aria-hidden="true"
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      isSelected
                        ? "border-orange-500 bg-orange-500 text-white"
                        : "border-muted-foreground/30"
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })
          )}
        </div>
        {hasSelection && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs h-7"
              onClick={() => {
                onChange([]);
                setSearch("");
              }}
            >
              Clear all
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Custom tooltip — colored label boxes near the hovered point
// ---------------------------------------------------------------------------

function ChartTooltipContent({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;

  return (
    <div className="flex flex-col gap-1.5 pointer-events-none">
      {payload.map((entry: any) => {
        if (entry.value == null || entry.value === 0) return null;
        return (
          <div
            key={entry.dataKey}
            className="rounded px-2 py-1 text-xs font-semibold text-white shadow-md"
            style={{ backgroundColor: COLORS[entry.dataKey] ?? COLORS.sales }}
          >
            {formatCurrency(entry.value)}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SalesCommissionChart({
  tramsAccountId,
  viewMode,
  userRole,
  returnDateFrom,
  returnDateTo,
}: SalesCommissionChartProps) {
  const isPrinting = useSnapshotPrint();
  const { dateFrom: defaultFrom, dateTo: defaultTo } = useMemo(
    () => currentYearRange(),
    [],
  );

  const [filters, setFilters] = useState<ChartFilters>({
    dateFrom: defaultFrom,
    dateTo: defaultTo,
    suppliers: [],
    travelTypes: [],
    advisorIds: [],
    showSales: true,
    showCommission: true,
    includeOpen: false,
  });

  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});
  const [filterOpen, setFilterOpen] = useState(false);

  const hasCompanyAccess = useMemo(
    () => canManageAgencyUsers(userRole),
    [userRole],
  );

  /** Net Advisor Commission line (TRAMS agentAmount) — Agency Owners only. */
  const isAgencyOwner = useMemo(() => isAgencyOwnerRole(userRole), [userRole]);

  const advisorFilterAvailable = hasCompanyAccess && viewMode === "all";

  const debouncedFiltersRef = useRef(filters);
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    if (!advisorFilterAvailable) {
      setFilters((f) =>
        f.advisorIds.length > 0 ? { ...f, advisorIds: [] } : f,
      );
    }
  }, [advisorFilterAvailable]);

  useEffect(() => {
    debouncedFiltersRef.current = filters;
    const timer = setTimeout(() => {
      setDebouncedFilters(debouncedFiltersRef.current);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters]);

  const chartParams = useMemo(
    () => ({
      tramsAccountId,
      viewMode,
      dateFrom: debouncedFilters.dateFrom || undefined,
      dateTo: debouncedFilters.dateTo || undefined,
      returnDateFrom: returnDateFrom || undefined,
      returnDateTo: returnDateTo || undefined,
      suppliers: debouncedFilters.suppliers.length > 0 ? debouncedFilters.suppliers : undefined,
      travelTypes: debouncedFilters.travelTypes.length > 0 ? debouncedFilters.travelTypes : undefined,
      advisorIds:
        advisorFilterAvailable && debouncedFilters.advisorIds.length > 0
          ? debouncedFilters.advisorIds
          : undefined,
    }),
    [
      tramsAccountId,
      viewMode,
      debouncedFilters,
      advisorFilterAvailable,
      returnDateFrom,
      returnDateTo,
    ],
  );

  const { data: chartResponse, isLoading, error, isFetching } = useSalesCommissionChart(chartParams);
  const { data: filterOptions } = useChartFilterOptions({
    tramsAccountId,
    viewMode,
    returnDateFrom,
    returnDateTo,
  });

  const chartLabelsIncludeYear = useMemo(
    () =>
      !!debouncedFilters.dateFrom &&
      !!debouncedFilters.dateTo &&
      chartRangeSpansMultipleYears(
        debouncedFilters.dateFrom,
        debouncedFilters.dateTo,
      ),
    [debouncedFilters.dateFrom, debouncedFilters.dateTo],
  );

  const chartData = useMemo(() => {
    if (!chartResponse?.data) return [];
    return chartResponse.data.map((d) => {
      const paidComm = parseFloat(d.paidCommission) || 0;
      const openComm = parseFloat(d.openCommission) || 0;
      const paidSales = parseFloat(d.paidSales) || 0;
      const openSales = parseFloat(d.openSales) || 0;
      const paidAdvisorComm = parseFloat(d.paidAdvisorCommission ?? "") || 0;
      const openAdvisorComm = parseFloat(d.openAdvisorCommission ?? "") || 0;

      return {
        month: d.month,
        monthLabel: formatMonthLabel(d.month, chartLabelsIncludeYear),
        commission: filters.showCommission
          ? paidComm + (filters.includeOpen ? openComm : 0)
          : null,
        sales: filters.showSales
          ? paidSales + (filters.includeOpen ? openSales : 0)
          : null,
        advisorCommission: isAgencyOwner
          ? paidAdvisorComm + (filters.includeOpen ? openAdvisorComm : 0)
          : null,
      };
    });
  }, [
    chartResponse,
    filters.showCommission,
    filters.showSales,
    filters.includeOpen,
    chartLabelsIncludeYear,
    isAgencyOwner,
  ]);

  const handleLegendToggle = useCallback((key: string) => {
    setHiddenSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.suppliers.length > 0) count++;
    if (filters.travelTypes.length > 0) count++;
    if (advisorFilterAvailable && filters.advisorIds.length > 0) count++;
    if (filters.dateFrom !== defaultFrom || filters.dateTo !== defaultTo) count++;
    if (!filters.showSales) count++;
    if (!filters.showCommission) count++;
    if (filters.includeOpen) count++;
    return count;
  }, [filters, defaultFrom, defaultTo, advisorFilterAvailable]);

  const supplierOptions = useMemo(
    () => (filterOptions?.suppliers ?? []).map((s) => ({ value: s, label: s })),
    [filterOptions?.suppliers],
  );
  const travelTypeOptions = useMemo(
    () => (filterOptions?.travelTypes ?? []).map((t) => ({ value: t, label: t })),
    [filterOptions?.travelTypes],
  );
  const advisorOptions = useMemo(
    () => (filterOptions?.advisors ?? []).map((a) => ({ value: String(a.id), label: a.name })),
    [filterOptions?.advisors],
  );

  const hasData = chartData.length > 0 && chartData.some(
    (d) =>
      (d.commission ?? 0) > 0 ||
      (d.sales ?? 0) > 0 ||
      (d.advisorCommission ?? 0) > 0,
  );

  const hasHiddenOpenData = useMemo(() => {
    if (hasData || filters.includeOpen || !chartResponse?.data) return false;
    return chartResponse.data.some(
      (d) =>
        parseFloat(d.openCommission) > 0 || parseFloat(d.openSales) > 0,
    );
  }, [hasData, filters.includeOpen, chartResponse]);

  const commissionVisible =
    filters.showCommission && (isPrinting || !hiddenSeries.commission);
  const salesVisible = filters.showSales && (isPrinting || !hiddenSeries.sales);
  const advisorCommissionVisible =
    isAgencyOwner && (isPrinting || !hiddenSeries.advisorCommission);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="rounded-xl border bg-card p-5 print:p-3 snapshot-print-card snapshot-print-chart-card">
      {/* Top row: title, legend, filter icon.
          AR-316: below `sm` the title wrapped to three lines in a squeezed
          column while the legend sat beside it and orphaned the info icon, so
          the row stacks on narrow screens. */}
      <div className="mb-4 flex flex-col gap-2 print:mb-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="text-sm text-muted-foreground font-medium">
            Sales + Commission Chart
          </span>
          <InfoHint
            label="How the Sales + Commission chart is calculated"
            className="print:hidden"
          >
            <p>
              Sales and commission over time. Defaults to closed invoices
              year-to-date — use the filters to include open invoices. Gross
              commission means before your agency split.
              {isAgencyOwner &&
                " Agency Owners also see a net commission line, after agency split."}
            </p>
            <KpiBasisNote
              card="salesCommissionChart"
              override={chartResponse?.basisOverride?.note}
            />
          </InfoHint>
          <KpiBasisLabel
            card="salesCommissionChart"
            override={chartResponse?.basisOverride?.label}
          />
        </div>

        {/* Legend (static for PDF) */}
        <div className="hidden print:flex items-center gap-5 text-xs">
          {filters.showCommission && (
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: COLORS.commission }}
              />
              Gross Commission
            </span>
          )}
          {filters.showSales && (
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: COLORS.sales }}
              />
              Sales
            </span>
          )}
          {isAgencyOwner && (
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: COLORS.advisorCommission }}
              />
              Net Commission
            </span>
          )}
        </div>

        {/* AR-316: legend + filter share one row on narrow screens so the
            header is two lines, not three. */}
        <div className="flex items-center justify-between gap-3 sm:contents">
        <div className="flex items-center gap-5 print:hidden">
          <button
            type="button"
            className={`flex min-h-11 items-center gap-1.5 text-xs transition-opacity md:min-h-0 ${
              hiddenSeries.commission ? "opacity-30" : "opacity-100"
            }`}
            aria-pressed={!hiddenSeries.commission}
            onClick={() => handleLegendToggle("commission")}
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: COLORS.commission }}
            />
            Gross Commission
          </button>
          <button
            type="button"
            className={`flex min-h-11 items-center gap-1.5 text-xs transition-opacity md:min-h-0 ${
              hiddenSeries.sales ? "opacity-30" : "opacity-100"
            }`}
            aria-pressed={!hiddenSeries.sales}
            onClick={() => handleLegendToggle("sales")}
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: COLORS.sales }}
            />
            Sales
          </button>
          {isAgencyOwner && (
            <button
              type="button"
              className={`flex items-center gap-1.5 text-xs transition-opacity ${
                hiddenSeries.advisorCommission ? "opacity-30" : "opacity-100"
              }`}
              aria-pressed={!hiddenSeries.advisorCommission}
              onClick={() => handleLegendToggle("advisorCommission")}
            >
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: COLORS.advisorCommission }}
              />
              Net Commission
            </button>
          )}
          {isFetching && !isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </div>

        {/* Filter icon */}
        <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`relative p-1.5 rounded-md max-md:min-h-11 max-md:min-w-11 max-md:inline-flex max-md:items-center max-md:justify-center hover:bg-muted transition-colors print:hidden ${
                activeFilterCount > 0 ? "text-orange-600" : "text-muted-foreground"
              }`}
              aria-label="Chart filters"
            >
              <Filter className="h-4.5 w-4.5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-500 text-[9px] font-bold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end" aria-labelledby="chart-filters-title">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p id="chart-filters-title" className="text-sm font-semibold">Chart Filters</p>
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() =>
                      setFilters({
                        dateFrom: defaultFrom,
                        dateTo: defaultTo,
                        suppliers: [],
                        travelTypes: [],
                        advisorIds: [],
                        showSales: true,
                        showCommission: true,
                        includeOpen: false,
                      })
                    }
                  >
                    Reset all
                  </Button>
                )}
              </div>

              {/* Date range */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <CalendarRange className="h-3 w-3" />
                  Return Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    className="h-8 text-xs"
                    aria-label="Return date from"
                    value={filters.dateFrom}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, dateFrom: e.target.value }))
                    }
                  />
                  <Input
                    type="date"
                    className="h-8 text-xs"
                    aria-label="Return date to"
                    value={filters.dateTo}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, dateTo: e.target.value }))
                    }
                  />
                </div>
              </div>

              {/* Toggles */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 accent-orange-600"
                  checked={filters.showSales}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, showSales: e.target.checked }))
                  }
                />
                <span className="text-xs font-medium">Show Sales</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 accent-orange-600"
                  checked={filters.showCommission}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, showCommission: e.target.checked }))
                  }
                />
                <span className="text-xs font-medium">Show Gross Commission</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 accent-orange-600"
                  checked={filters.includeOpen}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, includeOpen: e.target.checked }))
                  }
                />
                <span className="text-xs font-medium">Include Open Invoices</span>
              </label>

              {/* Categorical filters */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Supplier</label>
                <MultiSelectFilter
                  label="Select suppliers"
                  options={supplierOptions}
                  selected={filters.suppliers}
                  onChange={(v) => setFilters((f) => ({ ...f, suppliers: v }))}
                  searchPlaceholder="Search suppliers..."
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Travel Type</label>
                <MultiSelectFilter
                  label="Select travel types"
                  options={travelTypeOptions}
                  selected={filters.travelTypes}
                  onChange={(v) => setFilters((f) => ({ ...f, travelTypes: v }))}
                  searchPlaceholder="Search travel types..."
                />
              </div>
              {hasCompanyAccess && viewMode === "all" && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Advisor</label>
                  <MultiSelectFilter
                    label="Select advisors"
                    options={advisorOptions}
                    selected={filters.advisorIds.map(String)}
                    onChange={(v) =>
                      setFilters((f) => ({ ...f, advisorIds: v.map(Number) }))
                    }
                    searchPlaceholder="Search advisors..."
                  />
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
        </div>
      </div>

      {/* Active filter pills — included in PDF; remove buttons hidden when printing */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4 snapshot-print-filter-pills">
          {filters.suppliers.map((s) => (
            <span
              key={`s-${s}`}
              className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
            >
              {s}
              <button type="button" aria-label={`Remove ${s} filter`} onClick={() => setFilters((f) => ({ ...f, suppliers: f.suppliers.filter((v) => v !== s) }))}>
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
          {filters.travelTypes.map((t) => (
            <span
              key={`t-${t}`}
              className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {t}
              <button type="button" aria-label={`Remove ${t} filter`} onClick={() => setFilters((f) => ({ ...f, travelTypes: f.travelTypes.filter((v) => v !== t) }))}>
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
          {advisorFilterAvailable &&
            filters.advisorIds.map((id) => {
            const advisor = filterOptions?.advisors?.find((a) => a.id === id);
            return (
              <span
                key={`a-${id}`}
                className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
              >
                {advisor?.name ?? `Advisor #${id}`}
                <button type="button" aria-label={`Remove ${advisor?.name ?? `advisor ${id}`} filter`} onClick={() => setFilters((f) => ({ ...f, advisorIds: f.advisorIds.filter((v) => v !== id) }))}>
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            );
          })}
          {filters.includeOpen && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
              Open included
              <button type="button" aria-label="Remove open invoices filter" onClick={() => setFilters((f) => ({ ...f, includeOpen: false }))}>
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Chart area */}
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-[280px] w-full rounded-lg" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-[280px] text-center">
          <AlertCircle className="h-8 w-8 text-destructive/40 mb-2" />
          <p className="text-sm font-medium text-destructive">
            Failed to load chart data
          </p>
          <p className="text-xs text-muted-foreground mt-1 max-w-[300px]">
            {error instanceof Error ? error.message : "An unexpected error occurred"}
          </p>
        </div>
      ) : !hasData ? (
        <div className="flex flex-col items-center justify-center h-[280px] text-center">
          <BarChart3 className="h-8 w-8 text-muted-foreground/20 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">
            No data for this period
          </p>
          {hasHiddenOpenData ? (
            <p className="text-xs text-muted-foreground mt-1 max-w-[340px]">
              There are open (unpaid) invoices matching your filters.{" "}
              <button
                className="text-orange-600 hover:underline font-medium dark:text-orange-400"
                onClick={() => setFilters((f) => ({ ...f, includeOpen: true }))}
              >
                Include open invoices
              </button>{" "}
              to see them.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1 max-w-[300px]">
              Adjust the date range or filters, or create invoices to see trends here.
            </p>
          )}
        </div>
      ) : (
        <>
        {/* AR-210 (TC-13.1): the line chart had no text alternative. Screen readers
            get the same numbers as a table; the SVG itself is hidden from them. */}
        <table className="sr-only">
          <caption>Sales and commission by month</caption>
          <thead>
            <tr>
              <th scope="col">Month</th>
              {salesVisible && <th scope="col">Sales</th>}
              {commissionVisible && <th scope="col">Gross commission</th>}
              {advisorCommissionVisible && <th scope="col">Net commission</th>}
            </tr>
          </thead>
          <tbody>
            {chartData.map((d) => (
              <tr key={d.month}>
                <th scope="row">{d.monthLabel}</th>
                {salesVisible && <td>{formatCurrency(d.sales ?? 0)}</td>}
                {commissionVisible && <td>{formatCurrency(d.commission ?? 0)}</td>}
                {advisorCommissionVisible && <td>{formatCurrency(d.advisorCommission ?? 0)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="snapshot-print-chart" aria-hidden="true">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 12, left: 12, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="hsl(var(--border))"
              strokeOpacity={0.5}
            />
            <XAxis
              dataKey="monthLabel"
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={false}
              dy={8}
            />
            <YAxis
              tickFormatter={formatYAxisTick}
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={false}
              width={76}
            />
            <Tooltip
              content={<ChartTooltipContent />}
              cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "4 4" }}
              wrapperStyle={{ outline: "none" }}
            />

            {commissionVisible && (
              <Line
                type="linear"
                dataKey="commission"
                name="Gross Commission"
                stroke={COLORS.commission}
                strokeWidth={2}
                dot={{ r: 3, fill: COLORS.commission, stroke: COLORS.commission }}
                activeDot={{ r: 5, fill: COLORS.commission, stroke: "#fff", strokeWidth: 2 }}
                connectNulls
              />
            )}
            {salesVisible && (
              <Line
                type="linear"
                dataKey="sales"
                name="Sales"
                stroke={COLORS.sales}
                strokeWidth={2.5}
                dot={{ r: 3, fill: COLORS.sales, stroke: COLORS.sales }}
                activeDot={{ r: 5, fill: COLORS.sales, stroke: "#fff", strokeWidth: 2 }}
                connectNulls
              />
            )}
            {advisorCommissionVisible && (
              <Line
                type="linear"
                dataKey="advisorCommission"
                name="Net Commission"
                stroke={COLORS.advisorCommission}
                strokeWidth={2}
                dot={{ r: 3, fill: COLORS.advisorCommission, stroke: COLORS.advisorCommission }}
                activeDot={{ r: 5, fill: COLORS.advisorCommission, stroke: "#fff", strokeWidth: 2 }}
                connectNulls
              />
            )}
          </LineChart>
        </ResponsiveContainer>
        </div>
        </>
      )}
    </div>
  );
}
